import { captureException } from '@sentry/react'
import { useState } from 'react'
import { MdOutlineFileDownload } from 'react-icons/md'

import type { ReactElement } from 'react'
import type { FileWithPath } from 'react-dropzone'
import type { QUALITY_IDENTIFIER } from '~/const/asset'
import type { IAssetFile, IAssetFileQuality } from '~/types/assets-management'

import Button from '~/components/Buttons/Button'
import Dropzone from '~/components/Dropzone'
import FilePreview from '~/components/FilePreview'
import FilePreviewModal from '~/components/FilePreviewModal'
import ModalDialog from '~/components/ModalDialog'
import { useCreateAssetFileMutation, useUploadFileMutation } from '~/queries/files'
import { getQualityIdentifierFromFilename } from '~/utils/asset'

import './styles.scss'

interface Props {
  files: IAssetFile[]
  isEditable?: boolean
  onFilesUpload: (assetFileQuality: IAssetFileQuality[]) => void
  onQualityIdentifierChange: (assetFileQuality: IAssetFileQuality) => void
  onRemoveAllFiles: () => void
  onRemoveSingleFile: (id: string) => void
  withDropzone?: boolean
}

type FilePreview = { downloadUrl: string; fileType: string }

const AssetDetailsFileManagement = ({
  files,
  isEditable,
  onFilesUpload,
  onQualityIdentifierChange,
  onRemoveAllFiles,
  onRemoveSingleFile,
  withDropzone,
}: Props): ReactElement => {
  const [fileToRemove, setFileToRemove] = useState<string | undefined>()

  const [filePreview, setFilePreview] = useState<FilePreview>()

  const [isRemoveAllFilesDialogOpen, setRemoveAllFilesDialogOpen] = useState(false)

  const { mutateAsync: createAssetFileMutation } = useCreateAssetFileMutation()

  const { mutateAsync: uploadFileMutation } = useUploadFileMutation()

  const [isFileUploading, setFileUploading] = useState(false)

  const handleQualityIdentifierChange = (id: string, qualityIdentifier: QUALITY_IDENTIFIER) => {
    onQualityIdentifierChange({ file_id: id, qualifier: qualityIdentifier })
  }

  const handleDrop = async (files: FileWithPath[]) => {
    const uploadFile = async (file: FileWithPath) => {
      const { file_id, upload_url } = await createAssetFileMutation({
        mimeType: file.type,
        name: file.name,
        size: file.size,
      })

      await uploadFileMutation({
        file,
        mimeType: file.type,
        uploadUrl: upload_url,
      })

      return { file_id, qualifier: getQualityIdentifierFromFilename(file.name) }
    }

    try {
      setFileUploading(true)

      const assetFileQuality = await Promise.all(
        files.map(async (file) => {
          return uploadFile(file)
        })
      )

      onFilesUpload(assetFileQuality)
    } catch (e) {
      captureException(e)
    } finally {
      setFileUploading(false)
    }
  }

  const handleRemoveSingleFile = () => {
    onRemoveSingleFile(fileToRemove as string)
    setFileToRemove(undefined)
  }

  const toggleRemoveAllFilesDialog = (isOpen: boolean) => {
    return () => setRemoveAllFilesDialogOpen(isOpen)
  }

  const handleRemoveAllFiles = () => {
    onRemoveAllFiles()
    setRemoveAllFilesDialogOpen(false)
  }

  const handleAssetFilePreviewClick = (filePreview: FilePreview) => {
    setFilePreview(filePreview)
  }

  const handleAssetFilePreviewClose = () => {
    setFilePreview(undefined)
  }

  return (
    <div className="asset-details-file-management-container">
      <div className="button-container">
        {isEditable && files.length > 0 ? (
          <Button onClick={toggleRemoveAllFilesDialog(true)} outlined size="small">
            Remove all
          </Button>
        ) : null}
      </div>
      <div className="dropzone-files-container">
        {withDropzone && (
          <Dropzone
            className="asset-details-dropzone"
            icon={<MdOutlineFileDownload color="#aeaeae" size={80} />}
            isLoading={isFileUploading}
            multiple
            onDrop={handleDrop}
          />
        )}
        <div className="asset-details-files-container">
          {files.map(({ download_url, id, mime_type, name, quality_identifier }) => (
            <FilePreview
              isEditable={isEditable}
              key={id}
              mimeType={mime_type}
              name={name}
              onClick={() => handleAssetFilePreviewClick({ downloadUrl: download_url, fileType: mime_type })}
              onQualityIdentifierChange={(qualityIdentifier) => handleQualityIdentifierChange(id, qualityIdentifier)}
              onRemove={withDropzone ? () => setFileToRemove(id) : undefined}
              qualityIdentifier={quality_identifier}
              src={download_url}
            />
          ))}
        </div>
      </div>
      <ModalDialog
        isOpen={!!fileToRemove}
        mainText="Remove asset file"
        onClose={() => setFileToRemove(undefined)}
        onConfirm={handleRemoveSingleFile}
        subText="Removing an asset file will permanently remove it from the database."
      />
      <ModalDialog
        isOpen={isRemoveAllFilesDialogOpen}
        mainText="Remove all asset files"
        onClose={toggleRemoveAllFilesDialog(false)}
        onConfirm={handleRemoveAllFiles}
        subText="Removing an asset file will permanently remove it from the database."
      />
      <FilePreviewModal
        downloadUrl={filePreview?.downloadUrl || ''}
        fileType={filePreview?.fileType || ''}
        isOpen={!!filePreview}
        onClose={handleAssetFilePreviewClose}
      />
    </div>
  )
}

export default AssetDetailsFileManagement
